import type {
    CoreSignals,
    CoreState,
    ElementaryGraphFunction,
    ElementaryService,
    GetAudioResource,
    ShowUserNotification,
} from "@/client/core/Core.api";
import {
    createSilentGraph,
    injectTransportMeterNodes,
    kTransportBpmMeterID,
    kTransportPlaybackMeterID,
    kTransportTimeSigDenominatorMeterID,
    kTransportTimeSigNumeratorMeterID,
} from "@/client/core/audio/Audio";
import {
    webBpmSignal,
    webIsPlayingSignal,
    webTimeSignatureSignal,
} from "@/client/core/audio/host-signals.audio";
import {
    keepAudioContextRunning,
    requestPlaybackAudioSession,
} from "@/client/core/audio/website-audio-unlock";
import {
    getLabsMeterEvent,
    getGlobalBeatClockPosition,
    kGlobalClockName,
    type LabsElementaryGraph,
} from "@/client/core/platform-types/labs.api";
import { decodeAudioFileToChannels } from "@/client/utils/decodeAudio";
import { showError, showErrorFactory } from "@/client/utils/error-handling";
import { isSidepanelHost } from "@/client/utils/getAppEnvironment";

import type WebRenderer from "@splice/elemaudio-web-renderer";

type VirtualFileSystem = Record<string, Float32Array[]>;

export const isVirtualFileSystem = (value: unknown): value is VirtualFileSystem => {
    if (value === null || typeof value !== "object") {
        return false;
    }
    for (const channels of Object.values(value as Record<string, unknown>)) {
        if (!Array.isArray(channels)) {
            return false;
        }
        for (const channel of channels) {
            if (!(channel instanceof Float32Array)) {
                return false;
            }
        }
    }
    return true;
};

const loadWebRenderer = async (audioContext: AudioContext): Promise<WebRenderer> => {
    const { default: WebRendererClass } = await import("@splice/elemaudio-web-renderer");

    const wasmUrl = new URL("/elementary-wasm.wasm", window.location.origin).href;

    const renderer = new WebRendererClass();
    const node = await renderer.initialize(audioContext, {
        numberOfInputs: 0,
        numberOfOutputs: 1,
        outputChannelCount: [2],
        processorOptions: {
            wasmBinaryUrl: wasmUrl,
        },
    });
    node.connect(audioContext.destination);
    return renderer;
};

export const createElementaryServiceForWebsite = (
    audioContext: AudioContext,
    state: CoreState,
    signals: CoreSignals,
    getAudioResource: GetAudioResource,
    showUserNotification: ShowUserNotification,
): ElementaryService => {
    const rendererPromise = loadWebRenderer(audioContext);

    // The plugin's Labs tab shares this service; its webview has no silent switch to get past.
    if (!isSidepanelHost()) {
        requestPlaybackAudioSession(navigator);
    }
    keepAudioContextRunning(audioContext, document);

    rendererPromise.catch((reason: unknown) => {
        // eslint-disable-next-line no-console
        console.error("Failed to load Elementary Audio web renderer", reason);
    });

    // Serialize all renders so note-on's render always completes before note-off's render
    // starts. This guarantees the audio worklet processes at least one frame with gate=1
    // before seeing gate=0, preventing silence on quick taps in gate (OneShot) mode.
    let renderQueue: Promise<void> = Promise.resolve();

    let lastGraph: LabsElementaryGraph = createSilentGraph();

    const setGraph: ElementaryGraphFunction = (labsGraph: LabsElementaryGraph) => {
        lastGraph = labsGraph;
        const snapshot: LabsElementaryGraph = {
            nodes: [...labsGraph.nodes],
            resourceIDs: labsGraph.resourceIDs,
        };
        renderQueue = renderQueue
            .then(async () => {
                const core = await rendererPromise;

                if (snapshot.resourceIDs.length > 0) {
                    const existingResourceIDs = await core.listVirtualFileSystem();
                    if (!Array.isArray(existingResourceIDs)) {
                        // eslint-disable-next-line no-console
                        console.error("[Elementary] VFS returned non-array:", existingResourceIDs);
                        return;
                    }

                    const newFileSystem: VirtualFileSystem = {};
                    let didAdd = false;
                    for (const id of snapshot.resourceIDs) {
                        if (existingResourceIDs.includes(id.value)) {
                            continue;
                        }
                        const audioResource = getAudioResource(id);
                        if (audioResource === undefined) {
                            showError(
                                showUserNotification,
                                "[Elementary] resource not found",
                                id.value,
                            );
                            continue;
                        }
                        const channels = await decodeAudioFileToChannels(audioResource.file);
                        newFileSystem[id.value] = channels;
                        didAdd = true;
                    }

                    if (didAdd) {
                        await core.updateVirtualFileSystem(newFileSystem);
                    }
                }

                injectTransportMeterNodes(snapshot);

                await core.render(...snapshot.nodes);
            })
            .catch((reason: unknown) => {
                // eslint-disable-next-line no-console
                console.error("[Elementary] setGraph error", reason);
            });
    };

    const reRenderLastGraph = () => {
        setGraph(lastGraph);
    };

    // Reset el.time() on play/stop so the beat clock always starts from 0,
    // then re-render so the meter nodes see the fresh transport values.
    webIsPlayingSignal.listen(() => {
        // eslint-disable-next-line no-console
        console.info("[Elementary] webIsPlayingSignal changed:", webIsPlayingSignal.value);
        void rendererPromise.then(async (core) => {
            await core.setCurrentTimeMs(0);
            reRenderLastGraph();
        });
    });
    webBpmSignal.listen(reRenderLastGraph);
    webTimeSignatureSignal.listen(reRenderLastGraph);

    rendererPromise
        .then((core) => {
            core.on("meter", (event) => {
                if (event.source === undefined) {
                    return;
                }
                const meterEvent = getLabsMeterEvent(event);
                if (meterEvent === undefined) {
                    return;
                }

                switch (meterEvent.id) {
                    case kTransportBpmMeterID: {
                        if (meterEvent.max !== state.transport.bpm && meterEvent.max > 0) {
                            signals.transport.bpm.notify(meterEvent.max);
                        }
                        break;
                    }
                    case kTransportPlaybackMeterID: {
                        const isPlaying = meterEvent.max > 0.5;
                        if (isPlaying !== state.transport.isPlaying) {
                            signals.transport.isPlaying.notify(isPlaying);
                        }
                        break;
                    }
                    case kTransportTimeSigNumeratorMeterID: {
                        const numerator = Math.round(meterEvent.max);
                        if (numerator !== state.transport.timeSignature.numerator) {
                            signals.transport.timeSignature.notify({
                                ...state.transport.timeSignature,
                                numerator,
                            });
                        }
                        break;
                    }
                    case kTransportTimeSigDenominatorMeterID: {
                        const denominator = Math.round(meterEvent.max);
                        if (denominator !== state.transport.timeSignature.denominator) {
                            signals.transport.timeSignature.notify({
                                ...state.transport.timeSignature,
                                denominator,
                            });
                        }
                        break;
                    }
                    case kGlobalClockName: {
                        const position = getGlobalBeatClockPosition(meterEvent);
                        if (
                            position !== undefined &&
                            position.value !== state.transport.position_beats
                        ) {
                            signals.transport.position_beats.notify(position.value);
                        }
                        break;
                    }
                    default:
                        signals.meterEvents.notify(meterEvent);
                        break;
                }
            });
        })
        .catch(showErrorFactory(showUserNotification, "Elementary Website graph"));

    return { setGraph };
};
